import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { List, ListItem, SearchInput, Title } from '@icarros/ids-react';
import { useAdContext } from '../../../store/contexts/ad';
import { ListItemModifier, OpenEditTitleWrapper, OpenEditWrapper } from './style';
import { useAd } from '../../../store/hooks/useAd';
import { Loading } from '../../../components/Loading';
import { SaveFormContent } from './SaveFormContent';
import { getFilteredContent } from '../../../utils/getFilteredContent';
import { ResultsNotFound } from '../../../components/ResultsNotFound';
import { MultipleEdits } from './components/Modal';
import { EDIT_ROUTES } from '../../../utils/adEditRoutes';
import { isLongList, isMediumList } from '../../../utils/boolean';

export function ModelYearForm(): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const { getCachedContent, setCurrentContent, loading, initialLoading, error } = useAd();
  const {
    currentAd,
    setValueIsDifferentFromCurrent,
    setCurrentWarningMessage,
    cumulativeChanges,
    setCurrentTabDepth
  } = useAdContext();
  const { anoModelo, modelo } = currentAd?.veiculo || {};
  const [selectedRadio, setSelectedRadio] = useState(
    cumulativeChanges?.veiculo?.anoModelo ? cumulativeChanges?.veiculo?.anoModelo : anoModelo
  );
  const [searchField, setSearchField] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const fabricationYearUrl = location.pathname.replace(
    EDIT_ROUTES.modelYear.name,
    EDIT_ROUTES.fabricationYear.name
  );

  const modelYears = getCachedContent<'modelYears'>('modelYears', {
    path: 'getModelYears',
    dispatchType: 'SET_MODEL_YEARS',
    serviceParams: {
      modelId: modelo?.id
    }
  });

  const modelYearsAsObject = modelYears?.map(item => ({ nome: String(item) }));
  const filteredModelYears =
    searchField !== ''
      ? modelYearsAsObject && getFilteredContent(modelYearsAsObject, searchField)
      : modelYearsAsObject;

  useEffect(() => {
    setValueIsDifferentFromCurrent(selectedRadio !== anoModelo);
  }, [selectedRadio, anoModelo, setValueIsDifferentFromCurrent]);

  useEffect(() => {
    if (error?.fieldMissing) {
      setModalOpen(true);
      setCurrentWarningMessage({
        title: 'deseja confirmar as alterações?',
        body: 'Para salvar a alteração do ano do modelo do seu veículo você precisará confirmar também o ano de fabricação e a versão.',
        redirect: fabricationYearUrl
      });
    }
  }, [error, fabricationYearUrl, setCurrentWarningMessage]);

  return (
    <OpenEditWrapper>
      <OpenEditTitleWrapper>
        <Title size="overline">editar dados do veículo</Title>
        <Title variant="default" contrast="light">
          qual o ano do modelo do seu carro?
        </Title>
        {isLongList(modelYears?.length) && (
          <SearchInput
            contrast="default"
            placeholder="buscar ano do modelo"
            onChange={event => setSearchField(event.target.value)}
          />
        )}
      </OpenEditTitleWrapper>
      {(initialLoading || loading) && <Loading backgroundOnly={loading} />}

      {!initialLoading &&
        modelYears &&
        (!filteredModelYears || filteredModelYears.length === 0) && <ResultsNotFound />}

      <List>
        {filteredModelYears ? (
          filteredModelYears.map(({ nome }) => {
            const yearAsNumber = Number(nome);
            return (
              <ListItemModifier key={nome} isNormalCase>
                <ListItem
                  label={nome}
                  variant="selector"
                  radioOnChange={() => setSelectedRadio(yearAsNumber)}
                  radioValue={nome}
                  radioChecked={yearAsNumber === selectedRadio}
                  selectorName={nome}
                  selectorVariant="radio"
                />
              </ListItemModifier>
            );
          })
        ) : (
          <></>
        )}
      </List>

      {!(initialLoading && selectedRadio === anoModelo) && (
        <SaveFormContent
          loading={loading}
          isDisabled={selectedRadio === anoModelo}
          isBody={!isMediumList(modelYears?.length)}
          handleClick={() => {
            setCurrentContent({
              veiculo: {
                anoModelo: selectedRadio
              }
            });
          }}
        />
      )}

      <MultipleEdits
        title="deseja editar o ano do modelo?"
        body="Ao editar o ano do modelo do seu veículo você precisará confirmar também o ano de fabricação e a versão."
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleContinue={() => {
          setCurrentTabDepth(state => state + 1);
          history.push(fabricationYearUrl);
        }}
        handleBack={() => {
          setModalOpen(false);
        }}
      />
    </OpenEditWrapper>
  );
}
